import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import SvgIcon from '@material-ui/core/SvgIcon';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ListItemText from '@material-ui/core/ListItemText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const DropdownItem = styled(MenuItem)`
  && {
    font-size: ${props => props.theme.typography.body2.fontSize};
  }
`;

const DropdownArrow = styled.span`
  position: absolute;
  font-size: 7px;
  width: 3em;
  height: 3em;

  &::before {
    content: '';
    margin: auto;
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
  }
`;

const DropdownPopper = styled(Popper)`
  && {
    z-index: 1;

    &[x-placement*='bottom'] .arrow {
      top: 0;
      left: 0;
      margin-top: -0.9em;
      width: 3em;
      height: 1em;

      &::before {
        border-width: 0 1em 1em 1em;
        border-color: transparent transparent ${props => props.theme.palette.common.white}
          transparent;
      }
    }

    &[x-placement*='top'] .arrow {
      left: 0;
      bottom: 0;
      margin-bottom: -0.9em;
      width: 3em;
      height: 1em;

      &::before {
        border-width: 1em 1em 0 1em;
        border-color: ${props => props.theme.palette.common.white} transparent transparent
          transparent;
      }
    }

    &[x-placement*='right'] .arrow {
      left: 0;
      margin-left: -0.9em;
      height: 3em;
      width: 1em;
      &::before {
        border-width: 1em 1em 1em 0;
        border-color: transparent ${props => props.theme.palette.common.white} transparent
          transparent;
      }
    }
    &[x-placement*='left'] .arrow {
      right: 0;
      margin-right: -0.9em;
      height: 3em;
      width: 1em;
      &::before {
        border-width: 1em 0 1em 1em;
        border-color: transparent transparent transparent
          ${props => props.theme.palette.common.white};
      }
    }
  }
`;

const Dropdown = ({ items, id, icon, position, disablePortal, variant, label, maxHeight }) => {
  const [open, setOpen] = useState(false);
  const anchorEl = useRef(null);
  const [arrowRef, setArrowRef] = useState(null);

  const handleToggle = () => setOpen(!open);

  const handleClose = e => {
    if (anchorEl.current.contains(e.target)) {
      return;
    }
    setOpen(false);
  };

  const handleArrowRef = node => setArrowRef(node);

  const getTransformOrigin = currentPlacement => {
    const replacements = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
    };

    const re = new RegExp(Object.keys(replacements).join('|'), 'gi');
    let transformOrigin = currentPlacement.replace(re, match => replacements[match]);

    if (transformOrigin.includes('bottom') || transformOrigin.includes('top')) {
      transformOrigin = transformOrigin.replace('end', 'right').replace('start', 'left');
    } else if (transformOrigin.includes('left') || transformOrigin.includes('right')) {
      transformOrigin = transformOrigin.replace('end', 'bottom').replace('start', 'top');
    }

    return transformOrigin.replace('-', ' ');
  };

  return (
    <>
      {variant === 'iconButton' && (
        <IconButton onClick={handleToggle} buttonRef={anchorEl} color="inherit">
          {icon || (
            <SvgIcon fontSize="small">
              <FontAwesomeIcon fixedWidth icon={['fal', 'ellipsis-v']} />
            </SvgIcon>
          )}
        </IconButton>
      )}
      {variant === 'button' && (
        <Button
          variant="contained"
          color="primary"
          aria-haspopup="true"
          onClick={handleToggle}
          buttonRef={anchorEl}
          startIcon={icon}
        >
          {label}
          <SvgIcon fontSize="small">
            <FontAwesomeIcon fixedWidth icon={['fal', 'angle-down']} />
          </SvgIcon>
        </Button>
      )}
      <DropdownPopper
        open={open}
        anchorEl={anchorEl.current}
        transition
        placement={position}
        disablePortal={disablePortal}
        modifiers={{
          arrow: {
            enabled: true,
            element: arrowRef,
          },
        }}
      >
        {({ TransitionProps, placement }) => {
          const transformOrigin = getTransformOrigin(placement);

          return (
            <Grow {...TransitionProps} id={id} style={{ transformOrigin }}>
              <Paper style={{ minWidth: 150, maxHeight, overflow: 'auto' }}>
                <DropdownArrow className="arrow" ref={handleArrowRef} />
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList>
                    {items &&
                      items.map(({ text, action, color, disabled }) => {
                        const onClick = e => {
                          handleClose(e);
                          action();
                        };

                        return (
                          <DropdownItem dense key={text} onClick={onClick} disabled={disabled}>
                            <ListItemText
                              primary={text}
                              primaryTypographyProps={{ color: color || 'textPrimary' }}
                            />
                          </DropdownItem>
                        );
                      })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          );
        }}
      </DropdownPopper>
    </>
  );
};

Dropdown.defaultProps = {
  items: null,
  label: '',
  variant: 'iconButton',
  position: 'bottom-start',
  id: 'dropdown-menu',
  icon: undefined,
  disablePortal: true,
  maxHeight: 'auto',
};

const dropdownItemShape = PropTypes.shape({
  text: PropTypes.string,
  action: PropTypes.func,
  disabled: PropTypes.bool,
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'default',
    'initial',
    'inherit',
    'textPrimary',
    'textSecondary',
    'error',
  ]),
});

Dropdown.propTypes = {
  variant: PropTypes.oneOf(['iconButton', 'button']),
  label: PropTypes.string,
  icon: PropTypes.elementType,
  id: PropTypes.string,
  items: PropTypes.arrayOf(dropdownItemShape),
  position: PropTypes.oneOf([
    'top',
    'top-start',
    'top-end',
    'right',
    'right-start',
    'right-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
  ]),
  disablePortal: PropTypes.bool,
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Dropdown;
